import React, { useEffect, useState } from 'react';
import {
    Anchor,
    Button,
    Center,
    Container,
    Image,
    Notification,
    Paper,
    PasswordInput,
    Stack,
    TextInput,
    Title,
} from '@mantine/core';
import { useForm, zodResolver } from '@mantine/form';
import { inject, observer } from 'mobx-react';
import { usePostLoginMutation } from '../../global/entities/Login/mutations/usePostLoginMutation.tsx';
import appmedLogo from '../../assets/appMed.png';
import { IconAt, IconLock } from '@tabler/icons-react';
import * as z from 'zod';
import { t } from 'i18next';
import { AppMedError } from '../../global/error/AppMedError.ts';
import Shell from '../../global/component/organism/Shell/Shell.tsx';
import { useDisclosure } from '@mantine/hooks';
import { ForgotPasswordModal } from './ForgotPassword/ForgotPassword.tsx';
import { usePostForgotPasswordMutation } from '../../global/entities/Login/mutations/usePostForgetPasswordMutation.tsx';
import { useTranslation } from 'react-i18next';
import { useEmailErrorHandler } from '../../global/hooks/useEmailErrorHandler';
import { ClinicSelectorModal } from './ClinicSelector/ClinicSelectorModal.tsx';
import { CurrentUser } from '../../global/entities/Login/CurrentUser.ts';

function getReturnUrl(): string | null {
    const params = new URLSearchParams(window.location.search);
    const returnUrl = params.get('returnUrl');
    if (returnUrl && returnUrl.startsWith('/') && !returnUrl.startsWith('//'))
        return returnUrl;
    return null;
}

const LoginForm: React.FC = inject()(
    observer(() => {
        const [loginError, setLoginError] = useState<{ title: string; message: string } | null>(null);
        const [forgotPasswordError, setForgotPasswordError] = useState<string | null>(null);
        const [isSubmitting, setIsSubmitting] = useState(false);
        const [opened, { open, close }] = useDisclosure(false);
        const [pendingUser, setPendingUser] = useState<CurrentUser | null>(null);
        
        const handleModalClose = () => {
            close();
            setForgotPasswordError(null); // Clear forgot password errors when modal is closed
        };
        const { i18n } = useTranslation();
        const { handleEmailError } = useEmailErrorHandler();

        useEffect(() => {
            console.info('language login form:', i18n.language);
        }, [i18n.language]);

        const forgotPasswordMutation = usePostForgotPasswordMutation();
        const addCurrentUserMutation = usePostLoginMutation();
        const schema = z.object({
            email: z.string().email({ message: t('formError.emailIsRequired') }),
            password: z.string().min(1, { message: t('formError.passwordIsRequired') }),
        });
        const form = useForm({
            mode: 'uncontrolled',
            initialValues: {
                email: '',
                password: '',
            },
            validate: zodResolver(schema),
        });

        const handleSubmit = async (values: { email: string; password: string }) => {
            setLoginError(null);
            setIsSubmitting(true);
            try {
                const user = await addCurrentUserMutation.mutateAsync({
                    username: values.email,
                    password: values.password,
                });

                const facilities = user.healthcareFacilities;
                if (facilities && facilities.length > 1) 
                    setPendingUser(user);
                 else {
                    if (facilities && facilities.length === 1) {
                        user.selectedHealthcareFacilityID = facilities[0].id;
                        localStorage.setItem('currentUser', JSON.stringify(user));
                    }
                    window.location.href = getReturnUrl() || user.redirect;
                }
            } catch (e) {
                if (e instanceof AppMedError)
                    setLoginError({ title: e.title, message: e.message });
                 else
                    setLoginError({ title: 'Error', message: 'An unexpected error occurred.' });

            } finally {
                setIsSubmitting(false);
            }
        };

        const handleClinicSelect = (facilityId: number) => {
            if (!pendingUser) return;
            pendingUser.selectedHealthcareFacilityID = facilityId;
            localStorage.setItem('currentUser', JSON.stringify(pendingUser));
            window.location.href = getReturnUrl() || pendingUser.redirect;
        };

        const onSendForgetRequest = async (mail: string) => {
            setForgotPasswordError(null);
            setIsSubmitting(true);
            try {
                await forgotPasswordMutation
                    .mutateAsync({
                        mail: mail,
                    })
                    .then(() => {
                        close();
                        setForgotPasswordError(null); // Clear any previous errors on success
                    });
            } catch (e) {
                if (e instanceof AppMedError) {
                    const emailErrorInfo = handleEmailError(e);
                    setForgotPasswordError(emailErrorInfo?.message || 'An unexpected error occurred.');
                } else 
                    setForgotPasswordError('An unexpected error occurred.');
                
            } finally {
                setIsSubmitting(false);
            }
        };

        return (
            <Shell>
                <Container mt={0} mb={0} size={'default'} my={40} bg={'var(--mantine-color-gray-1)'} mih={'100vh'}>
                    <Stack align={'center'} w={'100%'} h={'auto'} gap={'sm'} justify={'flex-start'}>
                        <Center mt={'lg'}>
                            <Image w={206} h={90} src={appmedLogo} />
                        </Center>
                        <Title order={2} ta={'center'}>
                            {t('formGlobal.welcome')}
                        </Title>
                        <Paper maw={478} mt={'lg'}>
                            <form
                                onSubmit={form.onSubmit((values) => {
                                    handleSubmit(values);
                                })}
                            >
                                <Stack w={'100%'} gap={'md'}>
                                    <TextInput
                                        label={t('formGlobal.email')}
                                        placeholder={t('formGlobal.enterEmailAddress')}
                                        leftSection={<IconAt size={16} />}
                                        {...form.getInputProps('email')}
                                    />
                                    <PasswordInput
                                        label={t('formGlobal.password')}
                                        placeholder={t('formGlobal.enterPassword')}
                                        leftSection={<IconLock size={16} />}
                                        {...form.getInputProps('password')}
                                    />
                                    <Button fullWidth mt="sm" type="submit" loading={isSubmitting}>
                                        {t('formGlobal.signIn')}
                                    </Button>
                                    <Center>
                                        <Anchor onClick={open} size="sm">
                                            {t('anchor.forgot')}
                                        </Anchor>
                                    </Center>
                                </Stack>
                            </form>
                           {loginError && (
                                <Notification mt={'md'} color="red" onClose={() => setLoginError(null)} title={loginError.title}>
                                    {loginError.message}
                                </Notification>
                            )} 
                        </Paper>
                    </Stack>
                </Container>
                <ForgotPasswordModal
                    opened={opened}
                    onClose={handleModalClose}
                    onSend={onSendForgetRequest}
                    error={forgotPasswordError}
                    isSubmitting={isSubmitting}
                />
                {pendingUser && pendingUser.healthcareFacilities && (
                    <ClinicSelectorModal
                        opened={!!pendingUser}
                        facilities={pendingUser.healthcareFacilities}
                        onSelect={handleClinicSelect}
                    />
                )}
            </Shell>
        );
    }),
);

export default LoginForm;
